import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { T } from './theme';

const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase',
  color:T.inkSoft, fontWeight:600 };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px', font:'inherit',
  fontWeight:600, fontSize:13, cursor:'pointer',
  background: primary ? T.rose : '#fff', color: primary ? '#fff' : T.rose,
  border: primary ? 'none' : `1px solid ${T.b300}` });
const input = { border:`1px solid ${T.b200}`, borderRadius:8, padding:'8px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box', width:'100%' };

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { timeZone:'America/Los_Angeles', month:'short', day:'numeric' }); }
  catch { return String(d).slice(0, 10); }
}

// One editable field, driven by the form definition's field type.
function Field({ f, value, onChange, disabled }) {
  if (f.type === 'display') {
    return (
      <div style={{ background:T.bg, border:`1px solid ${T.b200}`, borderRadius:8,
        padding:'12px 14px', fontSize:12.5, lineHeight:1.6, color:T.inkSoft }}>{f.text || f.label}</div>
    );
  }
  const req = f.required ? <span style={{ color:T.rose }}> *</span> : null;
  if (f.type === 'yesno') {
    return (
      <div>
        <div style={{ ...label, marginBottom:6 }}>{f.label}{req}</div>
        <div style={{ display:'flex', gap:8 }}>
          {['yes','no'].map(v => (
            <button key={v} disabled={disabled} onClick={() => onChange(v)}
              style={{ ...btn(value === v), padding:'7px 22px', textTransform:'capitalize',
                opacity: disabled ? 0.6 : 1 }}>{v}</button>
          ))}
        </div>
      </div>
    );
  }
  if (f.type === 'date') {
    return (
      <div>
        <div style={{ ...label, marginBottom:6 }}>{f.label}{req}</div>
        <input type="date" style={{ ...input, maxWidth:220 }} value={value || ''}
          disabled={disabled} onChange={e => onChange(e.target.value)} />
      </div>
    );
  }
  // narrative + text + anything else: textarea for narrative, input otherwise
  if (f.type === 'narrative') {
    return (
      <div>
        <div style={{ ...label, marginBottom:6 }}>{f.label}{req}</div>
        {f.prompt ? <div style={{ fontSize:12, color:T.inkSoft, marginBottom:6 }}>{f.prompt}</div> : null}
        <textarea rows={3} style={{ ...input, resize:'vertical', lineHeight:1.5 }} value={value || ''}
          disabled={disabled} onChange={e => onChange(e.target.value)} />
      </div>
    );
  }
  return (
    <div>
      <div style={{ ...label, marginBottom:6 }}>{f.label}{req}</div>
      <input style={input} value={value || ''} disabled={disabled}
        onChange={e => onChange(e.target.value)} />
    </div>
  );
}

export default function ProviderQueue({ user, onOpenChart }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);          // { doc, fields, content, noteStatus }
  const [content, setContent] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pin, setPin] = useState('');
  const [signing, setSigning] = useState(false);
  const [signErr, setSignErr] = useState('');
  const [signedFlash, setSignedFlash] = useState('');
  const [sigEnrolled, setSigEnrolled] = useState(true);
  const [labOrders, setLabOrders] = useState([]);
  const [medOrders, setMedOrders] = useState([]);
  const [activatingMed, setActivatingMed] = useState(null);
  const [medErr, setMedErr] = useState('');
  const [activating, setActivating] = useState(null);

  const activateOrder = async (lo) => {
    setActivating(lo.order_id); setErr('');
    try {
      await axios.post(`${API}/api/emr/labs/orders/${lo.order_id}/review`);
      setSignedFlash(`Standing lab order activated for ${lo.first_name} ${lo.last_name}`);
      load();
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || 'Could not activate the order');
    }
    setActivating(null);
  };

  const [labResults, setLabResults] = useState([]);
  const [noDx, setNoDx] = useState([]); const [dxLimit, setDxLimit] = useState(24);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/emr/provider-queue`);
      setDocs(r.data.docs || []);
      setLabOrders(r.data.lab_orders || []);
      setMedOrders(r.data.med_orders || []);
      setLabResults(r.data.lab_results || []);
      setNoDx(r.data.no_diagnosis || []); setDxLimit(r.data.dx_limit_hours || 24);
      setErr('');
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || 'Could not load the provider queue');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const activateMedOrder = async (mo) => {
    setActivatingMed(mo.order_id); setMedErr('');
    try {
      await axios.post(`${API}/api/emr/med-orders/${mo.order_id}/review`);
      await load();
    } catch (e) {
      const d = (e.response && e.response.data) || {};
      if (d.code === 'dea_required') {
        const dea = window.prompt('This is a controlled substance order. Enter your DEA number to review it (2 letters + 7 digits):');
        if (dea && dea.trim()) {
          try {
            await axios.post(`${API}/api/emr/my-dea`, { dea_number: dea.trim() });
            await axios.post(`${API}/api/emr/med-orders/${mo.order_id}/review`);
            await load();
          } catch (e2) { setMedErr((e2.response && e2.response.data && e2.response.data.error) || 'DEA save failed'); }
        } else {
          setMedErr('Activation cancelled — a DEA number on file is required for controlled substance orders.');
        }
      } else {
        setMedErr(d.error || 'Could not activate that order');
      }
    }
    setActivatingMed(null);
  };
  useEffect(() => {
    axios.get(`${API}/api/emr/signature/status`)
      .then(r => setSigEnrolled(!!(r.data && r.data.enrolled)))
      .catch(() => {});
  }, []);

  const openDoc = async (d) => {
    setSignErr(''); setSignedFlash(''); setPin('');
    try {
      const [fr, nr] = await Promise.all([
        axios.get(`${API}/api/emr/forms/${d.form_key}`),
        axios.get(`${API}/api/emr/notes/${d.note_id}`)
      ]);
      const fields = (fr.data && fr.data.fields) || [];
      const versions = (nr.data && nr.data.versions) || [];
      const latest = versions.length ? versions[0] : null;
      const c = (latest && latest.content) || {};
      setOpen({ doc: d, fields });
      setContent({ ...c });
      setDirty(false);
    } catch (e) {
      setErr('Could not open that document');
    }
  };

  const setField = (key, val) => {
    setContent(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const save = async () => {
    if (!open) return null;
    setSaving(true); setSignErr('');
    try {
      await axios.post(`${API}/api/emr/notes/${open.doc.note_id}/versions`,
        { content, source: 'clinician_edit', diff_summary: 'Provider completion' });
      setDirty(false);
      setSaving(false);
      return true;
    } catch (e) {
      setSignErr((e.response && e.response.data && e.response.data.error) || 'Save failed');
      setSaving(false);
      return false;
    }
  };

  const sign = async () => {
    if (!open) return;
    setSigning(true); setSignErr('');
    try {
      if (dirty) {
        const ok = await save();
        if (!ok) { setSigning(false); return; }
      }
      await axios.post(`${API}/api/emr/notes/${open.doc.note_id}/sign`,
        { method:'pin', pin, attestation_text:'Provider completion and certification' });
      setSignedFlash(`${open.doc.title} signed for ${open.doc.first_name} ${open.doc.last_name}`);
      setOpen(null); setContent({}); setPin('');
      load();
    } catch (e) {
      const d = e.response && e.response.data;
      if (d && d.missing && d.missing.length) {
        setSignErr('Complete all required fields: ' + d.missing.map(m => m.replace(/^Required: /, '')).join(' · '));
      } else {
        setSignErr((d && d.error) || 'Sign failed');
      }
    }
    setSigning(false);
  };

  const missingRequired = open
    ? open.fields.filter(f => f.required && f.type !== 'display' &&
        (content[f.key] === undefined || content[f.key] === null || String(content[f.key]).trim() === '')).length
    : 0;

  if (loading) return <div style={{ padding:24, color:T.inkSoft }}>Loading provider queue…</div>;

  // ---------- DETAIL / COMPLETION VIEW ----------
  if (open) {
    const d = open.doc;
    return (
      <div style={{ maxWidth:760, margin:'0 auto', padding:'8px 4px 60px' }}>
        <button style={{ ...btn(false), marginBottom:14 }} onClick={() => { setOpen(null); setContent({}); }}>
          ← Back to queue
        </button>
        <div style={{ ...card, padding:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:4 }}>
            <div>
              <div style={{ fontSize:17, fontWeight:700, color:T.ink }}>{d.title}</div>
              <div style={{ fontSize:13, color:T.inkSoft, marginTop:3 }}>
                {d.first_name} {d.last_name} · {d.facility_name}{d.loc ? ` · ${d.loc}` : ''} · admitted {fmtDate(d.admit_date)}
              </div>
            </div>
            {onOpenChart ? (
              <button style={btn(false)} onClick={() => onOpenChart(d.patient_id)}>Open chart</button>
            ) : null}
          </div>
          <div style={{ borderTop:`1px solid ${T.b200}`, margin:'14px 0' }} />
          <div style={{ display:'grid', gap:16 }}>
            {open.fields.map(f => (
              <Field key={f.key} f={f} value={content[f.key]}
                onChange={v => setField(f.key, v)} disabled={saving || signing} />
            ))}
          </div>
          <div style={{ borderTop:`1px solid ${T.b200}`, margin:'18px 0 14px' }} />
          {!sigEnrolled ? (
            <div style={{ background:'#FFF7ED', border:'1px solid #FDBA74', borderRadius:8,
              padding:'12px 14px', fontSize:13, color:'#9A3412' }}>
              Signature enrollment required — draw your signature and set a PIN (Profile → Signature) before signing.
            </div>
          ) : (
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <button style={btn(false)} disabled={saving || !dirty} onClick={save}>
                {saving ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}
              </button>
              <div style={{ flex:1 }} />
              <input style={{ ...input, width:130, textAlign:'center', letterSpacing:'4px' }}
                type="password" inputMode="numeric" maxLength={4} placeholder="PIN"
                value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} />
              <button style={{ ...btn(true), opacity: (pin.length === 4 && missingRequired === 0 && !signing) ? 1 : 0.55 }}
                disabled={pin.length !== 4 || missingRequired > 0 || signing} onClick={sign}>
                {signing ? 'Signing…' : 'Sign & Finalize'}
              </button>
            </div>
          )}
          {missingRequired > 0 ? (
            <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:10 }}>
              {missingRequired} required field{missingRequired === 1 ? '' : 's'} remaining before signing.
            </div>
          ) : null}
          {signErr ? (
            <div style={{ marginTop:10, fontSize:13, color:'#B91C1C' }}>{signErr}</div>
          ) : null}
        </div>
      </div>
    );
  }

  // ---------- QUEUE LIST VIEW ----------
  return (
    <div style={{ maxWidth:860, margin:'0 auto', padding:'8px 4px 60px' }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:12, marginBottom:14 }}>
        <div style={{ fontSize:19, fontWeight:700, color:T.ink }}>Provider Queue</div>
        <div style={{ fontSize:13, color:T.inkSoft }}>
          {docs.length === 0 ? 'Nothing waiting' : `${docs.length} document${docs.length === 1 ? '' : 's'} awaiting provider completion`}
        </div>
      </div>
      {signedFlash ? (
        <div style={{ background:'#ECFDF5', border:'1px solid #6EE7B7', borderRadius:8,
          padding:'12px 14px', fontSize:13, color:'#065F46', marginBottom:14 }}>✓ {signedFlash}</div>
      ) : null}
      {err ? (
        <div style={{ background:'#FEF2F2', border:'1px solid #FCA5A5', borderRadius:8,
          padding:'12px 14px', fontSize:13, color:'#991B1B', marginBottom:14 }}>{err}</div>
      ) : null}
      {labOrders.length > 0 ? (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:8 }}>
            Standing Lab Orders — awaiting activation ({labOrders.length})
          </div>
          <div style={{ display:'grid', gap:10 }}>
            {labOrders.map(lo => (
              <div key={lo.order_id} style={{ ...card, padding:'14px 18px', display:'flex',
                alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:220 }}>
                  <div style={{ fontSize:14.5, fontWeight:700, color:T.ink }}>Standing Lab Order (Definitive Tox Panel)</div>
                  <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:3 }}>
                    {lo.first_name} {lo.last_name} · {lo.facility_name}{lo.loc ? ` · ${lo.loc}` : ''} · admitted {fmtDate(lo.admit_date)}
                    {lo.days_waiting > 2 ? ` · waiting ${lo.days_waiting} days` : ''}
                  </div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.6px',
                  background:'#DBEAFE', color:'#1E40AF', border:'1px solid #93C5FD',
                  borderRadius:999, padding:'4px 10px' }}>AWAITING ACTIVATION</span>
                <button style={{ ...btn(true), opacity: activating === lo.order_id ? 0.55 : 1 }}
                  disabled={activating === lo.order_id} onClick={() => activateOrder(lo)}>
                  {activating === lo.order_id ? 'Activating…' : 'Review & Activate'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {medOrders.length > 0 ? (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:8 }}>
            Medication Orders — awaiting review &amp; signature ({medOrders.length})
          </div>
          {medErr ? (
            <div style={{ background:'#FEF2F2', border:'1px solid #FCA5A5', borderRadius:8,
              padding:'10px 14px', fontSize:12.5, color:'#991B1B', marginBottom:10 }}>{medErr}</div>
          ) : null}
          <div style={{ display:'grid', gap:10 }}>
            {medOrders.map(mo => (
              <div key={mo.order_id} style={{ ...card, padding:'14px 18px', display:'flex',
                alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:220 }}>
                  <div style={{ fontSize:14.5, fontWeight:700, color:T.ink }}>
                    {mo.medication_name}
                    {mo.controlled_schedule ? <span style={{ marginLeft:8, fontSize:10.5, fontWeight:700,
                      background:'#FEF3C7', color:'#92400E', border:'1px solid #FCD34D',
                      borderRadius:999, padding:'2px 8px', verticalAlign:'middle' }}>{mo.controlled_schedule}</span> : null}
                  </div>
                  <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:3 }}>
                    {mo.first_name} {mo.last_name} · {mo.facility_name}{mo.loc ? ` · ${mo.loc}` : ''} · admitted {fmtDate(mo.admit_date)}
                    {mo.days_waiting > 0 ? ` · waiting ${mo.days_waiting} day${mo.days_waiting === 1 ? '' : 's'}` : ''}
                  </div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.6px',
                  background:'#FFE4E6', color:'#9F1239', border:'1px solid #FDA4AF',
                  borderRadius:999, padding:'4px 10px' }}>PENDING REVIEW</span>
                <button style={{ ...btn(true), opacity: activatingMed === mo.order_id ? 0.55 : 1 }}
                  disabled={activatingMed === mo.order_id} onClick={() => activateMedOrder(mo)}>
                  {activatingMed === mo.order_id ? 'Signing…' : 'Review & Activate'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {labResults.length > 0 ? (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:4 }}>
            Lab results to review ({labResults.length})
          </div>
          <div style={{ fontSize:12.5, color:T.inkSoft, marginBottom:8 }}>
            Open the chart's Labs tab to see the values and mark each result reviewed.
          </div>
          <div style={{ display:'grid', gap:10 }}>
            {labResults.map(x => (
              <div key={x.requisition_id} style={{ ...card, padding:'14px 18px', display:'flex',
                alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:220 }}>
                  <div style={{ fontSize:14.5, fontWeight:700, color:T.ink }}>{x.first_name} {x.last_name}</div>
                  <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:3 }}>
                    {x.facility_name}{x.loc ? ` · ${x.loc}` : ''} · specimen {x.specimen_id} · {x.results} result{x.results === 1 ? '' : 's'}
                    {x.received_at ? ` · received ${fmtDate(x.received_at)}` : ''}
                  </div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.6px',
                  background: x.abnormal_results > 0 ? '#FFE4E6' : '#FEF3C7', color: x.abnormal_results > 0 ? '#9F1239' : '#92400E',
                  border: `1px solid ${x.abnormal_results > 0 ? '#FDA4AF' : '#FCD34D'}`,
                  borderRadius:999, padding:'4px 10px' }}>{x.abnormal_results > 0 ? `${x.abnormal_results} ABNORMAL` : 'TO REVIEW'}</span>
                {onOpenChart ? (
                  <button style={btn(true)} onClick={() => onOpenChart(x.patient_id)}>Open chart</button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {noDx.length > 0 ? (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:4 }}>
            No diagnosis on chart ({noDx.length})
          </div>
          <div style={{ fontSize:12.5, color:T.inkSoft, marginBottom:8 }}>
            A diagnosis is due within {dxLimit} hours of admission. A stay cannot be billed without one.
          </div>
          <div style={{ display:'grid', gap:10 }}>
            {noDx.map(x => (
              <div key={x.episode_id} style={{ ...card, padding:'14px 18px', display:'flex',
                alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:220 }}>
                  <div style={{ fontSize:14.5, fontWeight:700, color:T.ink }}>{x.first_name} {x.last_name}</div>
                  <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:3 }}>
                    {x.facility_name}{x.loc ? ` · ${x.loc}` : ''} · admitted {fmtDate(x.admit_date)}
                    {x.hours_since_admit < 48 ? ` · ${x.hours_since_admit} hours ago` : ` · ${Math.floor(x.hours_since_admit / 24)} days ago`}
                  </div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.6px',
                  background: x.overdue ? '#FFE4E6' : '#FEF3C7', color: x.overdue ? '#9F1239' : '#92400E',
                  border: `1px solid ${x.overdue ? '#FDA4AF' : '#FCD34D'}`,
                  borderRadius:999, padding:'4px 10px' }}>{x.overdue ? 'OVERDUE' : 'DUE'}</span>
                {onOpenChart ? (
                  <button style={btn(true)} onClick={() => onOpenChart(x.patient_id)}>Open chart</button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {docs.length === 0 && labOrders.length === 0 && medOrders.length === 0 && noDx.length === 0 && labResults.length === 0 && !err ? (
        <div style={{ ...card, padding:'34px 20px', textAlign:'center', color:T.inkSoft, fontSize:14 }}>
          All provider documents are signed. New admits with provider forms will appear here automatically.
        </div>
      ) : docs.length > 0 ? (
        <div style={{ display:'grid', gap:10 }}>
          {docs.map(d => (
            <div key={d.note_id} style={{ ...card, padding:'14px 18px', display:'flex',
              alignItems:'center', gap:14, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:220 }}>
                <div style={{ fontSize:14.5, fontWeight:700, color:T.ink }}>{d.title}</div>
                <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:3 }}>
                  {d.first_name} {d.last_name} · {d.facility_name}{d.loc ? ` · ${d.loc}` : ''} · admitted {fmtDate(d.admit_date)}
                </div>
              </div>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.6px',
                background:'#FEF3C7', color:'#92400E', border:'1px solid #FCD34D',
                borderRadius:999, padding:'4px 10px' }}>AWAITING PROVIDER</span>
              <button style={btn(true)} onClick={() => openDoc(d)}>Complete &amp; Sign</button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
